import React, { useEffect, useState } from 'react';
import { Sparkles, X, Check } from 'lucide-react';
import advisor from '../../lib/advisor';

/**
 * CompanionCard — Release 0.6
 *
 * Micro-interacción determinista que resuelve una incertidumbre real del
 * usuario. No hay chat, no hay LLM. Cada opción escribe directamente al
 * Confidence Graph (nunca al Context — vía backend, gobernanza estricta).
 *
 * Props:
 *   - prompt: CompanionPrompt del backend
 *   - onResolved(prompt) — callback tras respuesta o descarte
 */
export default function CompanionCard({ prompt, onResolved }) {
  const [busy, setBusy] = useState(false);
  const [resolvedValue, setResolvedValue] = useState(null);

  useEffect(() => {
    if (!prompt?.id) return;
    // Fire-and-forget: le decimos al backend que el prompt fue mostrado.
    advisor.markCompanionShown(prompt.id).catch(() => {});
  }, [prompt?.id]);

  if (!prompt) return null;

  const handleResolve = async (optionValue) => {
    if (busy) return;
    setBusy(true);
    setResolvedValue(optionValue);
    try {
      await advisor.resolveCompanionPrompt(prompt.id, optionValue);
    } finally {
      setBusy(false);
      onResolved?.(prompt, optionValue);
    }
  };

  const handleDismiss = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await advisor.dismissCompanionPrompt(prompt.id);
    } finally {
      setBusy(false);
      onResolved?.(prompt, null);
    }
  };

  return (
    <div
      data-testid="companion-card"
      data-prompt-id={prompt.id}
      className="relative rounded-2xl bg-gradient-to-br from-habita-mint/40 to-white border border-habita/25 p-5 sm:p-6"
    >
      <button
        onClick={handleDismiss}
        disabled={busy}
        aria-label="Descartar sugerencia"
        data-testid="companion-dismiss"
        className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full text-gray-400 hover:text-habita-dark hover:bg-white/70 transition disabled:opacity-40"
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-full bg-habita text-white grid place-items-center">
          <Sparkles className="w-4 h-4" />
        </div>
        <p className="text-[11px] uppercase tracking-widest text-habita-dark/70 font-semibold">
          Habita Advisor · Ayúdame a entenderte mejor
        </p>
      </div>

      <p
        data-testid="companion-question"
        className="font-serif text-lg sm:text-xl text-habita-dark leading-snug mb-1"
      >
        {prompt.question}
      </p>

      {prompt.evidence_human && (
        <p
          data-testid="companion-evidence"
          className="text-xs text-gray-500 mb-4"
        >
          {prompt.evidence_human}
        </p>
      )}

      <div className="flex flex-wrap gap-2 mb-3" data-testid="companion-options">
        {prompt.options.map((o) => (
          <button
            key={o.value}
            onClick={() => handleResolve(o.value)}
            disabled={busy}
            data-testid={`companion-option-${o.value}`}
            className={`inline-flex items-center gap-1.5 h-9 px-4 rounded-full border transition text-sm font-medium ${
              resolvedValue === o.value
                ? 'bg-habita text-white border-habita'
                : 'bg-white text-habita-dark border-habita/40 hover:border-habita hover:bg-habita-mint/60'
            } disabled:opacity-60`}
          >
            {resolvedValue === o.value && <Check className="w-3.5 h-3.5" />}
            {o.label}
          </button>
        ))}
      </div>

      <p className="text-[11px] text-gray-500 leading-relaxed">
        <span className="font-semibold text-habita-dark/80">¿Por qué te lo preguntamos?</span>{' '}
        {prompt.user_value}
      </p>
    </div>
  );
}
